/**
 * Business dates are 'YYYY-MM-DD' strings without time or zone (inclusive ranges).
 * All helpers are pure: no "today" here. The server passes `asOf` explicitly.
 */
export type ISODate = string;

const RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

export function isISODate(s: unknown): s is ISODate {
  if (typeof s !== 'string') return false;
  const m = RE.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= daysInMonth(y, mo);
}

/** Days since 1970-01-01 (UTC calendar). */
export function toDayNumber(d: ISODate): number {
  const m = RE.exec(d);
  if (!m) throw new Error(`Invalid ISODate: ${d}`);
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}

export function fromDayNumber(n: number): ISODate {
  const dt = new Date(n * DAY_MS);
  const y = dt.getUTCFullYear();
  const mo = dt.getUTCMonth() + 1;
  const d = dt.getUTCDate();
  return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function addDays(d: ISODate, n: number): ISODate {
  return fromDayNumber(toDayNumber(d) + n);
}

/** Inclusive day count of [from, to]. Returns 0 if to < from. */
export function daysInclusive(from: ISODate, to: ISODate): number {
  const n = toDayNumber(to) - toDayNumber(from) + 1;
  return n > 0 ? n : 0;
}

export function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

export function daysInMonth(y: number, m: number): number {
  return [31, isLeapYear(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]!;
}

export function parts(d: ISODate): { y: number; m: number; d: number } {
  const mm = RE.exec(d);
  if (!mm) throw new Error(`Invalid ISODate: ${d}`);
  return { y: Number(mm[1]), m: Number(mm[2]), d: Number(mm[3]) };
}

export function make(y: number, m: number, d: number): ISODate {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Adds calendar months; day is clamped to the month's length (31.01. + 1 month = 28./29.02.). */
export function addMonths(d: ISODate, n: number): ISODate {
  const p = parts(d);
  const idx = p.y * 12 + (p.m - 1) + n;
  const y = Math.floor(idx / 12);
  const m = (idx % 12) + 1;
  return make(y, m, Math.min(p.d, daysInMonth(y, m)));
}

export const minDate = (a: ISODate, b: ISODate): ISODate => (a <= b ? a : b);
export const maxDate = (a: ISODate, b: ISODate): ISODate => (a >= b ? a : b);

export type Range = { from: ISODate; to: ISODate | null };

/** Intersection of two inclusive ranges (null `to` = open end). Returns null if empty. */
export function intersect(a: Range, b: Range): { from: ISODate; to: ISODate | null } | null {
  const from = maxDate(a.from, b.from);
  const to = a.to === null ? b.to : b.to === null ? a.to : minDate(a.to, b.to);
  if (to !== null && to < from) return null;
  return { from, to };
}

export function overlaps(a: Range, b: Range): boolean {
  return intersect(a, b) !== null;
}

export function contains(r: Range, d: ISODate): boolean {
  return d >= r.from && (r.to === null || d <= r.to);
}

/** Today's business date in a given IANA zone (server only; never used by the engine). */
export function todayIn(timeZone: string, now: Date): ISODate {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(now);
}
